import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { Reveal } from "./Reveal";

const FOREST = "#2E4A2E";
const HARVEST = "#2F6B3F";
const TERRA = "#C4562A";
const GOLD = "#C6A24B";

function CardShell({ title, kicker, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-border bg-cream-100 p-6 md:p-8">
      <p className="overline mb-2 text-gold">{kicker}</p>
      <h3 className="mb-6 font-display text-2xl font-medium text-forest md:text-3xl">{title}</h3>
      {children}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-sm border border-gold bg-forest px-3 py-2 font-mono text-xs text-cream-100">
      <div className="mb-1 uppercase tracking-wider text-gold">{label || payload[0].name}</div>
      {payload.map((p) => (
        <div key={p.name}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
}

export default function StatsCharts({ stats }) {
  if (!stats) return null;
  const barData = stats.varieties_per_fruit.map((f) => ({
    name: f.name, Indian: f.indian, Imported: f.imported, total: f.count,
  }));

  return (
    <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
      <Reveal>
        <CardShell
          testid="chart-varieties-per-fruit"
          kicker="Catalogue depth"
          title="Varieties tracked per fruit family"
        >
          <div className="h-[420px] w-full">
            <ResponsiveContainer width="100%" height="100%" minHeight={360} minWidth={0}>
              <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 24 }} barCategoryGap={6}>
                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#8A948A" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 12, fontFamily: "Manrope", fill: FOREST }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(46,74,46,0.05)" }} />
                <Bar dataKey="Indian" stackId="a" fill={HARVEST} />
                <Bar dataKey="Imported" stackId="a" fill={TERRA} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex gap-6 font-sans text-xs text-forest-light">
            <span className="flex items-center gap-2 text-harvest"><span className="h-2.5 w-2.5 bg-harvest" />Indian harvest</span>
            <span className="flex items-center gap-2 text-terracotta"><span className="h-2.5 w-2.5 bg-terracotta" />Imported window</span>
          </div>
        </CardShell>
      </Reveal>

      <div className="flex flex-col gap-6">
        <Reveal delay={0.1}>
          <CardShell testid="chart-origin-split" kicker="Two lines" title="Indian vs imported">
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <PieChart>
                  <Pie data={stats.origin_split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                    <Cell fill={HARVEST} />
                    <Cell fill={TERRA} />
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Reveal>

        <Reveal delay={0.2}>
          <CardShell testid="chart-reach" kicker="Sourcing reach" title="Where it comes from">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border bg-border">
              {[
                { v: stats.indian_states_count, l: "Indian growing states" },
                { v: stats.import_countries_count, l: "Import countries" },
                { v: stats.total_indian, l: "Indian varieties" },
                { v: stats.total_imported, l: "Imported varieties" },
              ].map((s) => (
                <div key={s.l} className="bg-cream-100 p-5">
                  <div className="font-display text-4xl font-semibold text-forest">{s.v}</div>
                  <div className="mt-1 font-sans text-xs text-forest-muted">{s.l}</div>
                </div>
              ))}
            </div>
          </CardShell>
        </Reveal>
      </div>
    </div>
  );
}
